import React, { useEffect, useMemo, useRef, useState } from "react";
import { string, shape, object } from "prop-types";
import get from "lodash/get";
import { useDispatch } from "react-redux";

import SideBarContent from "../../molecules/sidebar-content";

import Bredcrum from "../../story-templates/story-header/bredcrums";

import { AdContainer } from "../../ads";
import TaboolaAd from "../../ads/taboola-ad";
import TaboolaScript from "../../ads/taboola-ad/load-taboola-ad";
import PremiumIcon from "../../../components/atoms/other-icons/PremiumIcon";
import PremiumStoryBlock from "./premium-story-block";
import PremiumPagination from "./premium-pagination";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";

import { getCollection } from "../../helper/api";
import { setLoading } from "../../../components/store/actions";
import LatestNews from "../../story-templates/story-elements/latest-news";

import "./premium-page.m.css";
import useDevice from "../../helper/custom-hook/useDevice";
import CollectionWidget from "../../collection-templates/collection-widget";

const SideBarComponent = () => {
  const [showSidebar, setShowSideBar] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowSideBar(true);
    }, 3500);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="video-element">
      {showSidebar && <SideBarContent title="ಪ್ರಜಾವಾಣಿ ವಿಡಿಯೊ" speed={2500} type="video" />}
    </div>
  );
};

const STORIES_PER_PAGE = 10;

const getInitialStories = (collection) =>
  (collection?.items || [])
    .filter((item) => item.type === "story")
    .map((item) => item.story)
    .slice(0, STORIES_PER_PAGE);

const PremiumPage = ({ data }) => {
  const collectionSlug = data?.collection?.slug;
  const initialStories = useMemo(() => getInitialStories(data.collection), [data.collection]);

  const getPageFromUrl = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const pageFromUrl = parseInt(params.get("page"), 10);
      return !Number.isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
    }
    return 1;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [stories, setStories] = useState([]);
  const [isInitialPageLoading, setIsInitialPageLoading] = useState(true);
  const [renderTaboolaAd, setRenderTaboolaAd] = useState(false);
  const dispatch = useDispatch();
  const ref = useRef();
  const storyCacheRef = useRef({ 1: initialStories });
  const totalCount = get(data, ["collection", "total-count"], 0);
  const isMobile = useDevice();

  const customHeader = data?.collection?.metadata?.["custom-header"];
  const hideBannerForCustomHeader = customHeader === "Canara-Robeco";
  const shouldRenderTaboolaAd = !hideBannerForCustomHeader;

  const loadStories = async (page, options = {}) => {
    const shouldScroll = !options.skipScroll;

    const triggerScroll = () => {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }, 10);
    };

    if (!options.skipPushState && typeof window !== "undefined") {
      const url = new URL(window.location);
      if (page === 1) {
        url.searchParams.delete("page");
      } else {
        url.searchParams.set("page", page);
      }
      window.history.pushState(null, "", url.toString());
    }

    setCurrentPage(page);

    if (storyCacheRef.current[page]) {
      setStories(storyCacheRef.current[page]);
      if (shouldScroll) triggerScroll();
      return;
    }

    const offset = (page - 1) * STORIES_PER_PAGE;
    const slug = data.collection.slug;

    dispatch(setLoading(true));
    if (shouldScroll) triggerScroll();

    try {
      const nextStories = await getCollection(slug, offset, STORIES_PER_PAGE);
      storyCacheRef.current[page] = nextStories;
      setStories(nextStories);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const pageFromUrl = getPageFromUrl();
    // Server payload has first-page items, so cache those under page 1 only.
    storyCacheRef.current = { 1: initialStories };
    setIsInitialPageLoading(true);

    if (pageFromUrl > 1) {
      loadStories(pageFromUrl, { skipPushState: true, skipScroll: true }).finally(() => {
        setIsInitialPageLoading(false);
      });
      return;
    }

    setIsInitialPageLoading(false);
    setCurrentPage(1);
    setStories(initialStories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSlug, initialStories]);

  useEffect(() => {
    const handlePopState = () => {
      const pageFromUrl = getPageFromUrl();
      setCurrentPage(pageFromUrl);
      loadStories(pageFromUrl, { skipPushState: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) setRenderTaboolaAd(true);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const SectionName = data?.section?.name;
  const CollectionName = data?.collection?.name;

  const showHardcodedContent = data?.collection?.slug === "delhi-elections";

  const initialPath = typeof window !== "undefined" ? window.location.pathname : "";

  console.log(`[PremiumPage Debug] Render started. shouldRenderTaboolaAd: ${shouldRenderTaboolaAd}, renderTaboolaAd: ${renderTaboolaAd}`);

  return (
    <div styleName="premium-bg">
      <div className="container" styleName="breadcrumb-container">
        {!hideBannerForCustomHeader && <Bredcrum initialPath={initialPath} type="collection-page" isPremiumPage={true} />}
        {showHardcodedContent && <CollectionWidget isCustomContent={showHardcodedContent} />}
      </div>
      <div className="container" styleName="wrapper">
        {shouldRenderTaboolaAd && <TaboolaScript type="category" />}
        <div styleName="left-column">
          {!hideBannerForCustomHeader && (
            <div ref={ref} styleName="title-wrapper">
              <div styleName="title">
                <span styleName="premium-icon">
                  <PremiumIcon />
                </span>
                <h1>
                  <span>{SectionName || CollectionName || "Premium"}</span>
                </h1>
              </div>
              <div styleName="title-lines">
                <div styleName="line"></div>
                <div styleName="line"></div>
                <div styleName="line"></div>
              </div>
            </div>
          )}
          {isInitialPageLoading ? (
            <div styleName="premium-loader">
              <LoadingIndicatorComponent />
            </div>
          ) : (
            <>
              <div styleName="stories-container">
                <PremiumStoryBlock stories={stories.slice(0, 5)} />
                <PremiumStoryBlock stories={stories.slice(5, 10)} />
              </div>
              <PremiumPagination
                totalCount={totalCount}
                perPage={STORIES_PER_PAGE}
                currentPage={currentPage}
                loadStories={loadStories}
              />
              {!stories.length && <h1 styleName="text-info">No results available</h1>}
            </>
          )}
          {shouldRenderTaboolaAd && renderTaboolaAd ? (
            <div className="taboola">
              {!isMobile ? (
                <div className="hide-mobile">
                  <TaboolaAd
                    container="taboola-below-category-thumbnails"
                    placement="Below Category Thumbnails"
                    mode="thumbnails-a"
                  />
                </div>
              ) : (
                <div className="hide-desktop">
                  <TaboolaAd
                    container="taboola-below-category-thumbnails"
                    placement="Below Category Thumbnails"
                    mode="thumbnails-c-carousel"
                  />
                </div>
              )}
            </div>
          ) : null}
        </div>
        {!isMobile && (
          <div styleName="latest-wrapper-section">
            <div className="hide-mobile ad">
              <AdContainer AdClassName="standard-300x250" id="div-gpt-ad-1675836921322-0" desktop="PV_Desktop_HP_RT" />
            </div>

            <LatestNews />

            <div className="hide-mobile ad">
              <AdContainer AdClassName="standard-300x250" id="div-gpt-ad-1675837026472-0" desktop="PV_Desktop_HP_RM" />
            </div>
            {shouldRenderTaboolaAd && renderTaboolaAd ? (
              <div className="hide-mobile">
                <TaboolaAd
                  container="taboola-right-rail-thumbnails"
                  placement="Right Rail Thumbnails"
                  mode="thumbnails-rr"
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

PremiumPage.propTypes = {
  pageType: string,
  data: shape({
    collection: object,
    section: object
  })
};

export { PremiumPage };
